import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { useCurrentTalentProfile } from "@/shared/hooks";
import { useAuth } from "@/shared/stores/useAuth";
import useBookIconStore from "@/shared/stores/useBookIconStore";

import { useDeckCardThumbnails } from "./useDeckCardThumbnails";
import { DeckCardType, TalentItem } from "../types";
import {
  isDemoCard,
  isListeningTogetherCard,
  isSeasonCard,
  isSingleCard,
} from "../utils/card-utils";
import { useGetCardById } from "../api/taascard.api";
import { useGetCategories } from "@/cards/api/card.api";
import { CategoriesValue } from "@/shared";
import { PublishedCard } from "../types";
import { TEMPLATE_MAP } from "../constant/app-deck";
import { thumbnailRecordToPublishedCard } from "../types/app-deck/deckCardThumbnail.types";
import {
  isMainCardShellType,
  resolveMainCardShellPages,
} from "../utils/app-deck/mainCardShellReaders";
import { enhanceDemoCardForPreview } from "@/cards/demo-card/utils/demoCardPreviewUtils";
import { enhanceSeasonCardForPreview } from "@/cards/season-card/utils/seasonCardPreviewUtils";

/** URL tab keys for purchasable app decks (mirrors DeckCardLibrary). */
const DECK_TAB_PARAM: Partial<Record<DeckCardType, string>> = {
  Single_Card: "single_tab",
  Demo_Card: "demo_tab",
  Listening_Together_Card: "listening_together_tab",
  Season_Card: "season_tab",
};

const getPrimaryContent = (card: any) => {
  const content = card?.content ?? card?.pages?.[0]?.content ?? {};
  return content?.bmCard ?? content;
};

const getCollageCardTitle = (card: PublishedCard) => {
  const content = getPrimaryContent(card) as {
    title?: string;
    singleCard?: { title?: string };
  };
  return content?.singleCard?.title || content?.title || (card as { title?: string }).title || "";
};

export function useDeckCardLibrary(cardType: DeckCardType) {
  const { t } = useTranslation();
  const { isBookIconSelected, isCollageViewSelected } = useBookIconStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useAuth();
  const { currentProfile } = useCurrentTalentProfile();

  const isUserView = !currentProfile?.id;
  const isLoggedIn = Boolean(userInfo?.id);
  const isSeasonCardDeck = cardType === "Season_Card";

  const template = TEMPLATE_MAP[cardType as DeckCardType];

  // Available tab → purchased=false; Purchased tab uses OwnedDeckCardList (purchased=true).
  const tabParamKey = DECK_TAB_PARAM[cardType];
  const isPurchasedTab =
    isUserView &&
    isLoggedIn &&
    Boolean(tabParamKey) &&
    new URLSearchParams(location.search).get(tabParamKey!) === "purchased";

  const {
    thumbnailsByCardId,
    thumbnailRecords,
    isLoading: thumbnailsLoading,
  } = useDeckCardThumbnails({
    type: cardType,
    // Purchased tab mounts its own list fetch — skip the available query there.
    enabled: !isPurchasedTab,
    talentId: isUserView ? null : (currentProfile?.id ?? null),
    extraParams: isUserView && isLoggedIn ? { purchased: "false" } : undefined,
  });
  // ── Data transformation ────────────────────────────────────────────────────

  const rawCardsData = thumbnailRecords || [];

  const appendEndingCoverPage = (card: any, pages: any[] = []) => {
    const firstPageType = String(card?.type ?? pages?.[0]?.type ?? "")
      .replace(/[\s-]+/g, "_")
      .toLowerCase();
    const endingImageUrl = card?.ending_image_url ?? "";
    const supportsEndingCover =
      firstPageType === "single_card" ||
      firstPageType === "k_indie_card" ||
      firstPageType === "demo_card" ||
      firstPageType === "listening_together_card" ||
      firstPageType === "listen_together_card" ||
      firstPageType === "listeningtogethercard" ||
      firstPageType === "listentogethercard" ||
      firstPageType === "link_card" ||
      firstPageType === "linkcard";

    // Hide ending cover until the card is purchased.
    const isPurchased = (() => {
      const bought = Boolean(
        card?.purchase != null || card?.content?.is_purchased || pages?.[0]?.content?.is_purchased
      );
      if (firstPageType === "single_card" || firstPageType === "demo_card") {
        return bought;
      }
      if (firstPageType === "k_indie_card") {
        return Boolean(card?.is_k_indie_card_purchased);
      }
      if (
        firstPageType === "listening_together_card" ||
        firstPageType === "listen_together_card" ||
        firstPageType === "listeningtogethercard" ||
        firstPageType === "listentogethercard"
      ) {
        return Boolean(bought || card?.is_listening_together_card_purchased);
      }
      if (firstPageType === "link_card" || firstPageType === "linkcard") {
        return Boolean(bought || card?.is_link_card_purchased);
      }
      return false;
    })();

    const hasEndingCoverPage = pages.some((page) => {
      const pageType = String(page?.type ?? "")
        .replace(/[\s-]+/g, "_")
        .toLowerCase();
      const pageImageUrl =
        page?.content?.imageUrl ||
        (Array.isArray(page?.content?.url) ? page.content.url[0] : page?.content?.url) ||
        "";
      return pageType === "cover_image" && pageImageUrl === endingImageUrl;
    });
    if (!supportsEndingCover || !endingImageUrl || hasEndingCoverPage || !isPurchased) {
      return pages;
    }
    return [
      ...pages,
      {
        order: pages.length + 1,
        type: "cover_image",
        content: { imageUrl: endingImageUrl, url: endingImageUrl },
        assets: [],
      },
    ];
  };

  const transformedCards: PublishedCard[] = useMemo(() => {
    return thumbnailRecords.map(thumbnailRecordToPublishedCard);
  }, [thumbnailRecords]);

  const baseAllCards = transformedCards;

  const seasonDeckCards = useMemo(
    () => (isSeasonCardDeck ? baseAllCards.filter((card) => isSeasonCard(card)) : baseAllCards),
    [baseAllCards, isSeasonCardDeck]
  );

  const allCards = seasonDeckCards;

  // ── Extract unique talents ─────────────────────────────────────────────────

  const uniqueTalents = useMemo((): TalentItem[] => {
    const talentMap = new Map<number | string, TalentItem>();
    rawCardsData.forEach((card: any) => {
      const talent =
        card.talent ||
        card.pages?.[0]?.content?.talent ||
        card.pages?.[0]?.content?.instantCard?.talent ||
        card.pages?.[0]?.content?.buskingCard?.talent ||
        card.pages?.[0]?.content?.flashCard?.talent ||
        card.pages?.[0]?.content?.bmCard?.talent;
      if (talent?.id && !talentMap.has(talent.id)) {
        talentMap.set(talent.id, {
          id: talent.id,
          username: talent.username || "Unknown",
          profile_image: talent.profile_image || "",
        });
      }
    });
    return Array.from(talentMap.values());
  }, [rawCardsData]);

  // ── Talent filter ──────────────────────────────────────────────────────────

  const [selectedTalentId, setSelectedTalentId] = useState<number | string | null>(null);

  // ── Category and subcategory filters ──────────────────────────────────────

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSubCategories, setSelectedSubCategories] = useState<string[]>([]);

  const { data: categoriesResponse } = useGetCategories({
    key: ["categories"],
  });
  const categoryData = (categoriesResponse?.data || []) as CategoriesValue[];

  // ── Search ─────────────────────────────────────────────────────────────────

  const [searchQuery, setSearchQuery] = useState("");

  const searchFiltered = useMemo(() => {
    if (!searchQuery.trim()) return allCards;
    const q = searchQuery.toLowerCase();
    return allCards.filter((c) => {
      const content = c?.pages?.[0]?.content ?? {};
      const primaryContent = content?.bmCard ?? content;
      return (
        primaryContent?.title?.toLowerCase().includes(q) ||
        primaryContent?.description?.toLowerCase().includes(q)
      );
    });
  }, [allCards, searchQuery]);

  // ── Combined filtering ─────────────────────────────────────────────────────

  const filteredCards = useMemo(() => {
    let filtered = searchFiltered;

    // Talent filter
    if (selectedTalentId !== null) {
      filtered = filtered.filter((card) => {
        const talent =
          (card as any).talent ||
          card.pages?.[0]?.content?.talent ||
          card.pages?.[0]?.content?.instantCard?.talent ||
          card.pages?.[0]?.content?.buskingCard?.talent ||
          card.pages?.[0]?.content?.flashCard?.talent ||
          card.pages?.[0]?.content?.bmCard?.talent;
        return talent && String(talent.id) === String(selectedTalentId);
      });
    }

    return filtered;
  }, [
    searchFiltered,
    selectedTalentId,
    cardType,
    categoryData,
    selectedCategory,
    selectedSubCategories,
  ]);

  // ── Modal state ────────────────────────────────────────────────────────────

  const [selectedCard, setSelectedCard] = useState<PublishedCard | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const [isFlashOpen, setIsFlashOpen] = useState(false);

  const [isBuskingOpen, setIsBuskingOpen] = useState(false);

  const [isInstantOpen, setIsInstantOpen] = useState(false);
  const [isInstantBooking, setIsInstantBooking] = useState(false);

  const [expandCard, setExpandCard] = useState<PublishedCard | null>(null);
  const [isExpandOpen, setIsExpandOpen] = useState(false);
  const [cardDetailsFetchId, setCardDetailsFetchId] = useState<number | null>(null);

  const beginCardDetailsFetch = useCallback((card: PublishedCard) => {
    const id = Number(card.id);
    if (Number.isFinite(id) && id > 0) {
      setCardDetailsFetchId(id);
    }
  }, []);

  const clearCardDetailsFetch = useCallback(() => {
    setCardDetailsFetchId(null);
  }, []);

  const { data: detailsCardResponse } = useGetCardById({
    key: ["main-card-by-id", cardDetailsFetchId],
    params: {
      id: Number(cardDetailsFetchId ?? 0),
      ...(currentProfile?.id && {
        headers: { "talent-id": currentProfile.id.toString() },
      }),
    },
    options: {
      enabled: Boolean(cardDetailsFetchId) && !isExpandOpen,
      refetchOnMount: false,
      staleTime: 60_000,
    },
  });

  useEffect(() => {
    if (!isPreviewOpen && !isFlashOpen && !isBuskingOpen && !isInstantOpen) {
      clearCardDetailsFetch();
    }
  }, [isPreviewOpen, isFlashOpen, isBuskingOpen, isInstantOpen, clearCardDetailsFetch]);

  const lastProcessedIdRef = useRef<string | null>(null);

  // ── URL helpers ────────────────────────────────────────────────────────────

  const setIdInUrl = useCallback(
    (id: string) => {
      const sp = new URLSearchParams(location.search);
      sp.set("id", id);
      navigate({ pathname: location.pathname, search: sp.toString() }, { replace: true });
    },
    [location, navigate]
  );

  const removeIdFromUrl = useCallback(() => {
    const sp = new URLSearchParams(location.search);
    sp.delete("id");
    navigate({ pathname: location.pathname, search: sp.toString() }, { replace: true });
  }, [location, navigate]);

  const openCardExpand = useCallback(
    (card: PublishedCard) => {
      lastProcessedIdRef.current = String(card.id);
      setExpandCard(card);
      setIsExpandOpen(true);
      setIdInUrl(card.id);
    },
    [setIdInUrl]
  );

  const closeCardExpand = useCallback(() => {
    if (expandCard?.id) {
      // Keep ref set until URL id is removed so the deep-link effect does not
      // immediately re-open the modal while location.search still has ?id=.
      lastProcessedIdRef.current = String(expandCard.id);
    }
    setIsExpandOpen(false);
    setExpandCard(null);
    removeIdFromUrl();
  }, [removeIdFromUrl, expandCard?.id]);

  const dismissExpandForDetails = useCallback(() => {
    setIsExpandOpen(false);
    setExpandCard(null);
  }, []);

  const openCollageView = useCallback(
    (card: PublishedCard) => {
      if (isSingleCard(card)) {
        navigate(
          isUserView ? `/single-card/${card.id}/collage` : `/single-card/${card.id}/collage/talent`,
          { state: { card } }
        );
        return;
      }

      navigate("/collage-view-not-available", {
        state: {
          cardType,
          cardId: card.id,
          cardTitle: getCollageCardTitle(card),
          returnTo: `${location.pathname}${location.search}`,
        },
      });
    },
    [cardType, isUserView, location.pathname, location.search, navigate]
  );

  // ── Open card from URL param on mount / card-list change ──────────────────

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const idFromUrl = sp.get("id");
    if (!idFromUrl) {
      lastProcessedIdRef.current = null;
      return;
    }
    if (isPreviewOpen || isFlashOpen || isBuskingOpen || isInstantOpen) {
      return;
    }
    if (isExpandOpen && expandCard && String(expandCard.id) === idFromUrl) {
      lastProcessedIdRef.current = idFromUrl;
      return;
    }
    if (lastProcessedIdRef.current === idFromUrl) return;

    // Deep links should resolve even when the card is currently hidden by deck
    // tabs, search, or talent/category filters.
    const card = allCards.find((c) => String(c.id) === idFromUrl) ?? null;

    lastProcessedIdRef.current = idFromUrl;

    const isPurchasedTabOwnedDeepLink =
      (isSingleCard(card as PublishedCard) && sp.get("single_tab") === "purchased") ||
      (isSeasonCard(card as PublishedCard) && sp.get("season_tab") === "purchased") ||
      (isListeningTogetherCard(card as PublishedCard) &&
        sp.get("listening_together_tab") === "purchased");

    if (isPurchasedTabOwnedDeepLink) {
      return;
    }

    if (card) {
      openCardExpand(card);
    }
  }, [
    allCards,
    location.search,
    openCardExpand,
    isPreviewOpen,
    isFlashOpen,
    isBuskingOpen,
    isInstantOpen,
    isExpandOpen,
    expandCard,
  ]);

  useEffect(() => {
    const rawCard = detailsCardResponse?.data;
    if (!rawCard || !isPreviewOpen || !selectedCard) return;
    if (String(rawCard.id) !== selectedCard.id) return;

    const primaryContent = getPrimaryContent(rawCard);
    const contentPurchased = Boolean(primaryContent?.is_purchased ?? rawCard?.purchase != null);
    const useShellBridge =
      isMainCardShellType(rawCard?.type) ||
      cardType === "Single_Card" ||
      cardType === "Demo_Card" ||
      cardType === "Season_Card" ||
      cardType === "Impact_Card";
    const resolvedPages = useShellBridge
      ? resolveMainCardShellPages(rawCard, { isPurchased: contentPurchased })
      : rawCard.pages?.length
        ? rawCard.pages
        : rawCard.content
          ? [
              {
                id: rawCard.content?.id ?? rawCard.ref_id ?? 0,
                order: 1,
                type: rawCard.type ?? "",
                content: { ...rawCard.content, is_purchased: contentPurchased },
                assets: [],
              },
            ]
          : [];
    const refreshed: PublishedCard = {
      id: String(rawCard.id),
      title: rawCard.talent?.username
        ? `${rawCard.talent.username}'s Card`
        : `Card ${primaryContent?.title ?? primaryContent?.description ?? ""}`,
      coverImageUrl: rawCard.cover_image_url ?? "",
      endingImageUrl: rawCard.ending_image_url ?? "",
      template: rawCard.template ?? "",
      price: rawCard.price,
      deck_status: rawCard.deck_status,
      seal_time: rawCard.seal_time ?? primaryContent?.seal_time ?? null,
      is_k_indie_card_purchased: Boolean(rawCard?.is_k_indie_card_purchased),
      is_listening_together_card_purchased: Boolean(
        contentPurchased || rawCard?.is_listening_together_card_purchased
      ),
      is_link_card_purchased: Boolean(
        rawCard?.is_link_card_purchased ??
        (rawCard?.template === "Link Card Template" ? contentPurchased : false)
      ),
      type: "Custom" as const,
      createdAt: rawCard.created_at ?? "",
      pages: appendEndingCoverPage(rawCard, resolvedPages),
      talent: rawCard.talent,
      ...(rawCard.purchase != null ? { purchase: rawCard.purchase } : {}),
      ...(rawCard.content && typeof rawCard.content === "object"
        ? {
            content: {
              ...rawCard.content,
              is_purchased: contentPurchased,
            },
          }
        : {}),
      ...(Array.isArray(rawCard.table_of_contents)
        ? { table_of_contents: rawCard.table_of_contents }
        : {}),
    } as PublishedCard;
    const withDemoExps =
      cardType === "Demo_Card"
        ? enhanceDemoCardForPreview(refreshed, {
            exps: Array.isArray(primaryContent?.exps)
              ? primaryContent.exps
              : Array.isArray(rawCard?.exps)
                ? rawCard.exps
                : undefined,
            includeEpisodePages: !isUserView,
            isTalentView: !isUserView,
          })
        : cardType === "Season_Card"
          ? enhanceSeasonCardForPreview(refreshed, {
              exps: Array.isArray(primaryContent?.exps)
                ? primaryContent.exps
                : Array.isArray(rawCard?.exps)
                  ? rawCard.exps
                  : undefined,
              includeEpisodePages: !isUserView,
              isTalentView: !isUserView,
            })
          : refreshed;
    setSelectedCard(withDemoExps ?? refreshed);
  }, [detailsCardResponse, isPreviewOpen, selectedCard?.id, cardType, isUserView]);

  // ── Keep selected card in sync after refetch ──────────────────────────────

  useEffect(() => {
    if (selectedCard && allCards.length > 0) {
      const updated = allCards.find((c) => c.id === selectedCard.id);
      if (!updated) return;
      setSelectedCard((prev) => {
        if (!prev || prev.id !== updated.id) return updated;
        // List thumbnails are thinner than details — never wipe deck_status / price / pages.
        const prevContent = (prev as { content?: unknown }).content;
        const updatedContent = (updated as { content?: unknown }).content;
        return {
          ...updated,
          deck_status: updated.deck_status != null ? updated.deck_status : prev.deck_status,
          price: updated.price ?? prev.price,
          purchase: updated.purchase ?? prev.purchase,
          seal_time: updated.seal_time ?? prev.seal_time,
          pages: prev.pages?.length ? prev.pages : updated.pages,
          table_of_contents: updated.table_of_contents ?? prev.table_of_contents,
          ...(updatedContent != null || prevContent != null
            ? { content: updatedContent ?? prevContent }
            : {}),
        } as PublishedCard;
      });
    }
  }, [allCards, selectedCard?.id]);

  useEffect(() => {
    if (expandCard && allCards.length > 0) {
      const updated = allCards.find((c) => c.id === expandCard.id);
      if (!updated) return;
      setExpandCard((prev) => {
        if (!prev || prev.id !== updated.id) return updated;
        return {
          ...updated,
          deck_status: updated.deck_status != null ? updated.deck_status : prev.deck_status,
          price: updated.price ?? prev.price,
          purchase: updated.purchase ?? prev.purchase,
          seal_time: updated.seal_time ?? prev.seal_time,
          pages: prev.pages?.length ? prev.pages : updated.pages,
        } as PublishedCard;
      });
    }
  }, [allCards, expandCard?.id]);

  // ── Category / subcategory handlers ──────────────────────────────────────

  const handleCategoryClick = (slug: string) => {
    const isSameCategory = selectedCategory === slug;
    setSelectedCategory(isSameCategory ? null : slug);
    setSelectedSubCategories([]);
  };

  const handleSubCategoryClick = (slug: string) => {
    setSelectedSubCategories((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
  };

  // ── Card detail opener (book / collage / list modes) ─────────────────────

  const openCardDetails = useCallback(
    (card: PublishedCard) => {
      const sp = new URLSearchParams(location.search);
      const isPurchasedTabOwnedClick =
        (isSingleCard(card) && sp.get("single_tab") === "purchased") ||
        (isListeningTogetherCard(card) && sp.get("listening_together_tab") === "purchased");

      if (isPurchasedTabOwnedClick) return;

      if (isCollageViewSelected) {
        openCollageView(card);
        return;
      }

      if (isBookIconSelected) {
        beginCardDetailsFetch(card);
        setSelectedCard(card);
        setIsPreviewOpen(true);
        return;
      }

      if (isSeasonCard(card)) {
        beginCardDetailsFetch(card);
        setSelectedCard(card);
        setIsPreviewOpen(true);
        return;
      }

      if (isDemoCard(card)) {
        navigate(`/demo-card/${card.id}`, { state: { card } });
        return;
      }

      if (isListeningTogetherCard(card)) {
        navigate(`/listening-together-card/${card.id}`, { state: { card } });
        return;
      }

      beginCardDetailsFetch(card);
      setSelectedCard(card);
      setIsPreviewOpen(true);
    },
    [
      location.search,
      isCollageViewSelected,
      isBookIconSelected,
      openCollageView,
      navigate,
      beginCardDetailsFetch,
    ]
  );

  const handleCardClick = useCallback(
    (card: PublishedCard) => {
      const sp = new URLSearchParams(location.search);
      const isPurchasedTabOwnedClick =
        (isSingleCard(card) && sp.get("single_tab") === "purchased") ||
        (isListeningTogetherCard(card) && sp.get("listening_together_tab") === "purchased");

      if (isPurchasedTabOwnedClick) return;
      openCardExpand(card);
    },
    [location.search, openCardExpand]
  );

  // ── Return value ───────────────────────────────────────────────────────────

  return {
    // Loading
    loading: thumbnailsLoading,
    // Flags
    isUserView,
    filteredCards,
    uniqueTalents,
    thumbnailsByCardId,
    // Search
    searchQuery,
    setSearchQuery,
    // Talent filter
    selectedTalentId,
    setSelectedTalentId,
    // Category / subcategory
    selectedCategory,
    selectedSubCategories,
    categoryData,
    handleCategoryClick,
    handleSubCategoryClick,
    // Modal state
    selectedCard,
    setSelectedCard,
    isPreviewOpen,
    setIsPreviewOpen,
    isFlashOpen,
    setIsFlashOpen,
    isBuskingOpen,
    setIsBuskingOpen,
    isInstantOpen,
    setIsInstantOpen,
    isInstantBooking,
    expandCard,
    isExpandOpen,
    closeCardExpand,
    dismissExpandForDetails,
    openCardDetails,
    template,
    // Handlers
    handleCardClick,
    removeIdFromUrl,
    currentProfile,
  };
}
